import { useEffect, useState } from 'react'
import Hero from '../components/Hero'
import { useParams } from 'react-router-dom'
import {
    FaCheckCircle,
    FaHandHoldingHeart,
    FaHeart,
    FaMinus,
    FaMosque,
    FaPlus,
    FaStar,
} from 'react-icons/fa'
import DonationCard from '../components/DonationCard'
import { usePageTitle } from '../../shared/components/PageTitleContext'
import { useTranslation } from 'react-i18next'
import { useCart } from '../../util/CartContext'
import { fetchWithLanguage } from '../../util/fetchFunction'
import { languageUtil } from '../../util/languageUtils'
import { ErrorOverlay, Loading, LoadingOverlay } from '../../shared/components/Loading'
import { useCurrency } from '../../util/CurrencyContext'
import CurrencyFormat from '../components/CurrencyFormat'
import SuccessModal from '../components/SuccessModal'
import CartModal from '../components/CartModal'

const ReligiousDuesSlug = () => {
    const { id } = useParams()
    const [news, setNews] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
    const [zakatQuantity, setZakatQuantity] = useState(1)
    const [isZakatLoading, setIsZakatLoading] = useState(false)
    const [isCartModalOpen, setIsCartModalOpen] = useState(false)

    const { currencySymbol, currencyRate } = useCurrency()
    const { setTitle } = usePageTitle()
    const { t } = useTranslation()
    const { cart, addDonationItem } = useCart()
    const { language } = languageUtil()

    const onDonationChange = async data => {
        try {
            await addDonationItem({
                data,
                id,
                type: 'religious_due',
                onSuccess: () => setIsSuccessModalOpen(true),
                onError: error => () => {
                    setError(`Donation failed: ${error}`)
                    console.error('Donation failed:', error)
                },
            })
        } catch (error) {
            setError(`Unexpected error: ${error}`)
            console.error('Unexpected error:', error)
        }
    }

    const handleZakatSubmit = async () => {
        try {
            setIsZakatLoading(true)
            await addDonationItem({
                data: {
                    donation_type: 'religious_due',
                    amount: 5, // £5 per person
                    quantity: zakatQuantity,
                    frequent_option: 'single',
                },
                id, // This will be the parent_id for the Zakat project
                type: 'religious_due',
                onSuccess: () => {
                    setIsSuccessModalOpen(true)
                    setIsZakatLoading(false)
                },
                onError: error => {
                    console.error('Zakat donation failed:', error)
                    setError(`Zakat donation failed: ${error}`)
                    setIsZakatLoading(false)
                },
            })
        } catch (error) {
            console.error('Unexpected error:', error)
            setError(`Unexpected error: ${error}`)
            setIsZakatLoading(false)
        }
    }

    const handleQuantityChange = e => {
        const value = parseInt(e.target.value) || 1
        setZakatQuantity(value > 0 ? value : 1)
    }

    const incrementQuantity = () => {
        setZakatQuantity(prev => prev + 1)
    }

    const decrementQuantity = () => {
        setZakatQuantity(prev => (prev > 1 ? prev - 1 : 1))
    }

    useEffect(() => {
        const fetchNews = async () => {
            await fetchWithLanguage(`religious-due-detail/${id}`, setNews, setError, setLoading)
        }

        fetchNews()
    }, [id, language])

    useEffect(() => {
        if (news?.title) {
            setTitle(`Strive2Help - ${news.title || news.title_en}`)
        }
    }, [news?.title, setTitle])

    // Helper function to check if it's Zakat al-Fitr
    const isZakatAlFitr = () => {
        return news?.is_zakat === true || news?.is_zakat === '1' || news?.is_zakat === 1
    }

    // Zakat al-Fitr Card Component - Wide version
    const ZakatAlFitrCard = () => (
        <div className='w-full'>
            <div className='p-5 border-2 shadow-lg md:p-6 bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl border-emerald-200 shadow-emerald-200/30'>
                {/* Main content in vertical layout */}
                <div className='space-y-5 md:space-y-6'>
                    {/* Header section */}
                    <div className='relative text-center'>
                        <div className='inline-block px-3 pb-1 mb-2 border rounded-full bg-gradient-to-r from-emerald-100 to-green-100 border-emerald-300'>
                            <span className='text-xs font-semibold text-emerald-700'>
                                <FaStar className='pb-1 inline-block w-3 h-3 mr-1.5 align-middle' />
                                {t('donation.zakah.card.obligatoryCharity')}
                            </span>
                        </div>
                        <h3 className='mb-2 text-xl font-bold md:text-2xl text-emerald-900'>
                            {t('donation.zakah.card.title')}
                        </h3>
                        <div className='w-20 h-1 mx-auto rounded-full bg-gradient-to-r from-emerald-400 to-green-400'></div>
                    </div>

                    {/* Description */}
                    <div className='text-center'>
                        <p className='px-2 text-sm text-gray-700 md:text-base md:px-0'>
                            {t('donation.zakah.card.description', {
                                cost: `${currencySymbol}${(currencyRate * 5).toFixed(0)}`,
                            })}
                        </p>
                    </div>

                    {/* Benefits list - compact */}
                    <div className='flex flex-wrap justify-center gap-2'>
                        <div className='flex items-center gap-1.5 px-2.5 py-1 bg-emerald-100 rounded-full'>
                            <FaCheckCircle className='w-3 h-3 text-emerald-600' />
                            <span className='text-xs font-medium text-emerald-700'>
                                {t('donation.zakah.card.chips.3')}
                            </span>
                        </div>
                        <div className='flex items-center gap-1.5 px-2.5 py-1 bg-green-100 rounded-full'>
                            <FaCheckCircle className='w-3 h-3 text-green-600' />
                            <span className='text-xs font-medium text-green-700'>
                                <CurrencyFormat amount={5} decimal={0} />{' '}
                                {t('donation.zakah.card.chips.2')}
                            </span>
                        </div>
                        <div className='flex items-center gap-1.5 px-2.5 py-1 bg-emerald-100 rounded-full'>
                            <FaCheckCircle className='w-3 h-3 text-emerald-600' />
                            <span className='text-xs font-medium text-emerald-700'>
                                {t('donation.zakah.card.chips.1')}
                            </span>
                        </div>
                    </div>

                    {/* Donation form section */}
                    <div className='w-full max-w-md mx-auto'>
                        <div className='p-4 bg-white border shadow-sm md:p-5 rounded-xl border-emerald-200'>
                            {/* Quantity Input */}
                            <div className='mb-4'>
                                <label className='block mb-2 text-xs font-medium text-center text-gray-700'>
                                    {t('donation.zakah.card.labels.people')}
                                </label>
                                <div className='flex items-center justify-center gap-2 md:gap-3'>
                                    <button
                                        onClick={decrementQuantity}
                                        disabled={zakatQuantity <= 1}
                                        className='flex items-center justify-center text-lg font-bold text-white transition-all duration-200 rounded-full w-9 h-9 md:w-10 md:h-10 md:text-xl bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed'
                                    >
                                        <FaMinus />
                                    </button>

                                    <div className='relative'>
                                        <input
                                            type='number'
                                            min='1'
                                            value={zakatQuantity}
                                            onChange={handleQuantityChange}
                                            className='px-3 py-2 text-lg font-bold text-center border-2 rounded-lg w-28 md:w-32 md:px-4 md:py-3 md:text-xl border-emerald-300 focus:ring-2 focus:ring-emerald-500 focus:border-transparent'
                                        />
                                    </div>

                                    <button
                                        onClick={incrementQuantity}
                                        className='flex items-center justify-center text-lg font-bold text-white transition-all duration-200 rounded-full w-9 h-9 md:w-10 md:h-10 md:text-xl bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600'
                                    >
                                        <FaPlus />
                                    </button>
                                </div>
                            </div>

                            {/* Total Calculation */}
                            <div className='mb-5 text-center md:mb-6'>
                                <div className='mb-1 text-sm text-gray-600'>
                                    {t('donation.zakah.card.labels.total_amount')}
                                </div>
                                <div className='text-2xl font-bold md:text-3xl text-emerald-700'>
                                    <CurrencyFormat amount={5 * zakatQuantity} decimal={0} />
                                    {/* £{5 * zakatQuantity} */}
                                </div>
                                <div className='mt-1 text-xs text-gray-500'>
                                    {t('donation.zakah.card.labels.persons', {
                                        amount: `${currencySymbol}${(currencyRate * 5).toFixed(0)}`,
                                        quantity: zakatQuantity,
                                    })}
                                </div>
                            </div>

                            {/* Submit Button */}
                            <button
                                onClick={handleZakatSubmit}
                                disabled={isZakatLoading}
                                className='w-full py-2.5 md:py-3 text-base md:text-lg font-bold text-white transition-all duration-300 transform rounded-lg shadow-lg bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 hover:scale-[1.02] active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed'
                            >
                                {isZakatLoading ? (
                                    <div className='flex items-center justify-center gap-2'>
                                        <svg
                                            className='w-4 h-4 md:w-5 md:h-5 animate-spin'
                                            fill='none'
                                            viewBox='0 0 24 24'
                                        >
                                            <circle
                                                className='opacity-25'
                                                cx='12'
                                                cy='12'
                                                r='10'
                                                stroke='currentColor'
                                                strokeWidth='4'
                                            ></circle>
                                            <path
                                                className='opacity-75'
                                                fill='currentColor'
                                                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                            ></path>
                                        </svg>
                                        {t('cart.processing')}
                                    </div>
                                ) : (
                                    <div>
                                        {t('donation.zakah.card.labels.pay')}
                                        <CurrencyFormat amount={5 * zakatQuantity} decimal={0} />
                                    </div>
                                )}
                            </button>

                            {/* Islamic decorative pattern */}
                            <div className='mt-4 text-center text-emerald-300'>
                                <div className='text-lg md:text-xl'>۞ ۞ ۞</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )

    return (
        <div>
            {loading ? (
                <Loading />
            ) : (
                <div className='relative'>
                    {cart.isLoading && <LoadingOverlay />}
                    {error && <ErrorOverlay message={error} onClose={() => setError(null)} />}

                    <Hero
                        title={news.title || news.title_en}
                        imageUrl={news.header_image || news.image_preview}
                        size='large'
                    />

                    <SuccessModal
                        isOpen={isSuccessModalOpen}
                        onClose={() => setIsSuccessModalOpen(false)}
                        onOpenCart={() => setIsCartModalOpen(true)}
                    />
                    <CartModal isOpen={isCartModalOpen} onClose={() => setIsCartModalOpen(false)} />

                    {isZakatAlFitr() && (
                        <div className='w-full px-4 py-6 mx-auto md:max-w-4xl'>
                            <ZakatAlFitrCard />
                        </div>
                    )}

                    <div className='relative flex flex-col mx-auto lg:flex-row max-w-7xl'>
                        {/* Background decorative elements with Islamic/religious theme */}
                        <div className='absolute top-0 left-0 w-64 h-64 -translate-x-32 -translate-y-32 rounded-full bg-gradient-to-br from-emerald-100/20 to-green-100/20 blur-3xl'></div>
                        <div className='absolute bottom-0 right-0 translate-x-48 translate-y-48 rounded-full w-96 h-96 bg-gradient-to-br from-amber-100/20 to-yellow-100/20 blur-3xl'></div>
                        {/* Left Column - Donation Card (Sticky) */}
                        <div className='w-full px-4 py-8 lg:w-[400px] xl:w-[450px] lg:sticky top-24 self-start'>
                            <DonationCard donationData={news} onAmountChange={onDonationChange} />
                        </div>

                        {/* Right Column - Content */}
                        <div className='flex-1 max-w-3xl px-4 py-8 mx-auto lg:px-8 lg:py-12'>
                            {/* Subtitle with Islamic decorative element */}
                            {news.subtitle || news.subtitle_en ? (
                                <div className='relative mb-8 text-center lg:text-left'>
                                    <h2 className='relative inline-block text-3xl font-bold text-gray-900 lg:text-4xl'>
                                        <span className='relative z-10'>
                                            {news.subtitle || news.subtitle_en}
                                        </span>
                                        <span className='absolute left-0 w-full h-3 transform rounded-full -bottom-2 bg-gradient-to-r from-emerald-200/50 to-green-200/50 -rotate-1'></span>
                                    </h2>
                                    <div className='w-20 h-1 mx-auto mt-4 rounded-full lg:mx-0 bg-gradient-to-r from-emerald-400 to-green-400'></div>
                                </div>
                            ) : null}

                            {/* Description */}
                            <div className='mb-8'>
                                <div className='p-6 border shadow-sm lg:p-8 bg-gradient-to-br from-white to-emerald-50/30 rounded-2xl border-emerald-100'>
                                    <div className='flex items-start gap-3 mb-4'>
                                        <div className='w-2 h-2 mt-2 rounded-full bg-emerald-400'></div>
                                        <h3 className='text-lg font-semibold text-gray-900'>
                                            {t('donation.aboutDue')}
                                        </h3>
                                    </div>
                                    <div
                                        className='text-lg leading-relaxed text-gray-700'
                                        dangerouslySetInnerHTML={{
                                            __html: news.description || news.description_en,
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Urgency Message */}
                            {news.urgency_message && (
                                <div className='mb-8'>
                                    <div className='p-6 border shadow-sm bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200 rounded-2xl'>
                                        <div className='flex items-start gap-3 mb-3'>
                                            <div className='w-3 h-3 mt-1 rounded-full bg-amber-500 animate-pulse'></div>
                                            <h3 className='text-lg font-bold text-amber-900'>
                                                Important Notice
                                            </h3>
                                        </div>
                                        <div className='flex items-start gap-3'>
                                            <svg
                                                className='w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0'
                                                fill='currentColor'
                                                viewBox='0 0 20 20'
                                            >
                                                <path
                                                    fillRule='evenodd'
                                                    d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                                                    clipRule='evenodd'
                                                />
                                            </svg>
                                            <p className='font-medium text-amber-800'>
                                                {news.urgency_message || news.urgency_message_en}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Additional Content */}
                            {news.additional_content && (
                                <div className='mb-12'>
                                    <div className='p-6 bg-white border border-gray-200 shadow-sm lg:p-8 rounded-2xl'>
                                        <div className='flex items-start gap-3 mb-6'>
                                            <div className='w-2 h-2 mt-2 bg-green-400 rounded-full'></div>
                                            <h3 className='text-lg font-semibold text-gray-900'>
                                                {t('donation.info')}
                                            </h3>
                                        </div>
                                        <div className='prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-a:text-emerald-600 hover:prose-a:text-emerald-700'>
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        news.additional_content ||
                                                        news.additional_content_en,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Impact Sections */}
                            {news.impact_sections?.length > 0 && (
                                <section className='mt-16'>
                                    <div className='mb-10 text-center lg:text-left'>
                                        <h3 className='relative inline-block text-2xl font-bold text-gray-900 lg:text-3xl'>
                                            <span className='relative z-10'>
                                                {t('donation.impact')}
                                            </span>
                                            <span className='absolute left-0 w-full h-3 transform rounded-full -bottom-2 bg-gradient-to-r from-amber-200/50 to-yellow-200/50 -rotate-1'></span>
                                        </h3>
                                        <p className='max-w-2xl mt-4 text-gray-600'>
                                            {t('donation.impactDetails')}
                                        </p>
                                    </div>

                                    <div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'>
                                        {news.impact_sections.map((item, index) => (
                                            <div
                                                key={index}
                                                className='relative p-6 transition-all duration-300 border group bg-gradient-to-br from-white to-amber-50 rounded-xl border-amber-100 hover:border-amber-300 hover:-translate-y-1 hover:shadow-lg'
                                            >
                                                {/* Islamic decorative element */}
                                                <div className='absolute text-2xl opacity-50 -top-2 -right-2 text-amber-200'>
                                                    ۞
                                                </div>

                                                {/* Icon container */}
                                                <div className='relative mb-4'>
                                                    <div className='absolute inset-0 rounded-full bg-gradient-to-br from-amber-400/20 to-yellow-400/20 blur-md'></div>
                                                    <div className='relative flex items-center justify-center w-12 h-12 mx-auto rounded-full shadow-lg bg-gradient-to-br from-amber-500 to-yellow-500'>
                                                        {index === 0 ? (
                                                            <FaMosque className='text-xl text-white' />
                                                        ) : index === 1 ? (
                                                            <FaHandHoldingHeart className='text-xl text-white' />
                                                        ) : (
                                                            <FaHeart className='text-xl text-white' />
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Content */}
                                                <div className='space-y-3 text-center'>
                                                    <h4 className='text-lg font-bold text-gray-900'>
                                                        {item.title || item.title_en}
                                                    </h4>
                                                    <p className='text-gray-600'>
                                                        {item.description || item.description_en}
                                                    </p>
                                                </div>

                                                {/* Hover effect */}
                                                <div className='absolute inset-0 transition-opacity duration-300 border-2 opacity-0 pointer-events-none border-amber-300 rounded-xl group-hover:opacity-100'></div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {news.image_preview && (
                                <div className='mb-12'>
                                    <div className='relative overflow-hidden shadow-lg rounded-2xl group'>
                                        <img
                                            src={news.image_preview}
                                            alt={`${news.title || news.title_en}`}
                                            className='w-full h-auto transition-transform duration-700 group-hover:scale-105'
                                        />
                                        <div className='absolute inset-0 bg-gradient-to-t from-black/20 to-transparent'></div>
                                    </div>
                                </div>
                            )}

                            {/* Religious Guidance Section */}
                            <div className='p-8 mt-16 border bg-gradient-to-r from-emerald-50 to-green-50 rounded-2xl border-emerald-200 rtl:bg-gradient-to-l'>
                                <div className='flex flex-col items-center gap-6 lg:flex-row rtl:lg:flex-row-reverse'>
                                    {/* Icon & Title Section */}
                                    <div className='text-center lg:w-1/3 rtl:text-right lg:rtl:text-right'>
                                        <div className='inline-flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-gradient-to-br from-emerald-500 to-green-500'>
                                            <FaMosque className='w-8 h-8 text-white' />
                                        </div>
                                        <h4 className='text-xl font-bold text-emerald-900'>
                                            {t('donation.religiousSignificance')}
                                        </h4>
                                    </div>

                                    {/* Description Section */}
                                    <div className='lg:w-2/3'>
                                        <p className='text-justify text-emerald-800 rtl:text-right'>
                                            {t('donation.religiousDetails')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ReligiousDuesSlug
